// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import setDashboardIsHomepage from "@insite/client-framework/Store/Context/Handlers/SetDashboardIsHomepage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import { Roboto } from "@mcmc/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    dashboardIsHomepage: state.context.session.dashboardIsHomepage ?? undefined,
});

const mapDispatchToProps = {
    setDashboardIsHomepage,
    loadSession,
};

export interface MyAccountHomepageSelectorStyles {
    link?: LinkPresentationProps;
}

export const myAccountHomepageSelectorStyles: MyAccountHomepageSelectorStyles = {
    link: {
        typographyProps: { ...Roboto, size: 16, lineHeight: "21px", weight: 500 },
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            display: block;
            margin-left: auto;
        `,
    },
};

const styles = myAccountHomepageSelectorStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyAccountHomepageSelector = ({ dashboardIsHomepage, setDashboardIsHomepage, loadSession }: Props) => {
    const [localDashboardIsHomepage, localSetDashboardIsHomepage] = useState(dashboardIsHomepage);
    const toggleDashboardIsHomepage = () => {
        const newDashboardIsHomepage = !localDashboardIsHomepage;
        localSetDashboardIsHomepage(newDashboardIsHomepage);
        setDashboardIsHomepage({
            dashboardIsHomepage: newDashboardIsHomepage,
            onComplete: () => {
                loadSession();
            },
        });
    };

    return (
        <Link onClick={toggleDashboardIsHomepage} {...styles.link}>
            {localDashboardIsHomepage ? translate("Don't Make This My Homepage") : translate("Make This My Homepage")}
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountHomepageSelector),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        displayName: "Homepage Selector",
    },
};

export default widgetModule;
